import { MapPin } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import type { Project } from '../types';

interface ProjectMapViewProps {
  projects: Project[];
  onProjectSelect?: (project: Project) => void;
}

const ProjectMapView: React.FC<ProjectMapViewProps> = ({ projects, onProjectSelect }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const [isLoaded, setIsLoaded] = useState(false);

  // Load Google Maps script
  useEffect(() => {
    const initGoogleMaps = () => {
      if (window.google && window.google.maps) {
        setIsLoaded(true);
        return;
      }

      // Check if script is already loading
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        const checkGoogle = () => {
          if (window.google && window.google.maps) {
            setIsLoaded(true);
          } else {
            setTimeout(checkGoogle, 100);
          }
        };
        checkGoogle();
        return;
      }

      const script = document.createElement('script');
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

      if (!apiKey) {
        console.warn(
          'Google Maps API key not found. Please set VITE_GOOGLE_MAPS_API_KEY in your environment variables.'
        );
        return;
      }

      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.onload = () => setIsLoaded(true);
      script.onerror = () => console.error('Failed to load Google Maps script');
      document.head.appendChild(script);
    };

    initGoogleMaps();
  }, []);

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;

    // Default center (you can adjust this)
    const defaultCenter = { lat: 40.7128, lng: -74.006 }; // New York City

    const map = new google.maps.Map(mapRef.current, {
      zoom: 10,
      center: defaultCenter,
      styles: [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }],
        },
        {
          featureType: 'transit.station',
          stylers: [{ visibility: 'off' }],
        },
      ],
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });

    mapInstanceRef.current = map;

    // Create info window
    infoWindowRef.current = new google.maps.InfoWindow({
      disableAutoPan: false,
    });
  }, [isLoaded]);

  // Update markers when projects change
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Filter projects with valid coordinates
    const projectsWithCoords = projects.filter(
      project => project.location?.coordinates?.lat && project.location?.coordinates?.lng
    );

    if (projectsWithCoords.length === 0) return;

    const bounds = new google.maps.LatLngBounds();

    // Create markers for each project
    projectsWithCoords.forEach(project => {
      if (!project.location?.coordinates) return;

      const position = {
        lat: project.location.coordinates.lat,
        lng: project.location.coordinates.lng,
      };

      // Custom marker icon
      const markerIcon = {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: '#ef4444', // red-600
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
        strokeOpacity: 1,
      };

      const marker = new google.maps.Marker({
        position,
        map: mapInstanceRef.current,
        title: project.name,
        icon: markerIcon,
        animation: google.maps.Animation.DROP,
      });

      // Add click listener
      marker.addListener('click', () => {
        onProjectSelect?.(project);

        // Create HTML content for info window
        const createInfoWindowContent = (project: Project) => {
          const formatCurrency = (amount?: number) => {
            if (!amount) return 'N/A';
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(amount);
          };

          const formatDate = (date: Date | string) => {
            return new Date(date).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            });
          };

          const getStatusClass = (status: string) => {
            switch (status) {
              case 'planning':
                return 'bg-brand-100 text-brand-800';
              case 'active':
                return 'bg-green-100 text-green-800';
              case 'on_hold':
                return 'bg-yellow-100 text-yellow-800';
              case 'completed':
                return 'bg-gray-100 text-gray-800';
              case 'cancelled':
                return 'bg-red-100 text-red-800';
              default:
                return 'bg-gray-100 text-gray-800';
            }
          };

          const getPriorityColor = (priority: string) => {
            switch (priority) {
              case 'critical':
                return '#ef4444';
              case 'high':
                return '#f97316';
              case 'medium':
                return '#eab308';
              case 'low':
                return '#22c55e';
              default:
                return '#6b7280';
            }
          };

          const locationText =
            project.location?.address ||
            (project.location?.city && project.location?.state
              ? `${project.location.city}, ${project.location.state}`
              : 'Location not specified');

          return `
            <div style="max-width: 280px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              <!-- Header -->
              <div style="position: relative; height: 100px; background: linear-gradient(135deg, #fed7aa 0%, #fdba74 100%); border-radius: 8px 8px 0 0; overflow: hidden;">
                <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center;">
                  <div style="width: 40px; height: 40px; background: #ef4444; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 8px; color: white; font-weight: bold; font-size: 16px;">
                    ${project.name.charAt(0)}
                  </div>
                  <div style="display: flex; align-items: center; justify-content: center; gap: 4px;">
                    <div style="width: 8px; height: 8px; border-radius: 50%; background: ${getPriorityColor(project.priority)};"></div>
                    <span style="color: #c2410c; font-size: 11px; font-weight: 500; text-transform: uppercase;">${project.priority}</span>
                  </div>
                </div>
              </div>

              <!-- Content -->
              <div style="padding: 16px; background: white;">
                <!-- Title and Status -->
                <div style="margin-bottom: 12px;">
                  <h3 style="margin: 0 0 6px 0; font-size: 16px; font-weight: 600; color: #111827; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${project.name}</h3>
                  <span style="display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 500; ${getStatusClass(project.status).replace('bg-', 'background-color: ').replace('text-', 'color: ').replace('blue-100', '#dbeafe').replace('blue-800', '#1e40af').replace('green-100', '#dcfce7').replace('green-800', '#166534').replace('yellow-100', '#fef3c7').replace('yellow-800', '#92400e').replace('gray-100', '#f3f4f6').replace('gray-800', '#1f2937').replace('red-100', '#fee2e2').replace('red-800', '#991b1b')}">
                    ${project.status.charAt(0).toUpperCase() + project.status.slice(1).replace('_', ' ')}
                  </span>
                </div>

                <!-- Location -->
                <div style="display: flex; align-items: center; margin-bottom: 12px; font-size: 14px; color: #4b5563;">
                  <svg style="width: 16px; height: 16px; margin-right: 8px; color: #9ca3af;" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"></path>
                  </svg>
                  <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${locationText}</span>
                </div>

                <!-- Stats -->
                <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 12px; font-size: 14px;">
                  <div style="display: flex; align-items: center;">
                    <svg style="width: 16px; height: 16px; margin-right: 4px; color: #9ca3af;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                    </svg>
                    <span style="color: #4b5563;">${formatCurrency(project.budget)}</span>
                  </div>
                  <div style="display: flex; align-items: center;">
                    <svg style="width: 16px; height: 16px; margin-right: 4px; color: #9ca3af;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
                    </svg>
                    <span style="color: #4b5563;">${project.teamSize || 0} members</span>
                  </div>
                </div>

                <!-- Dates -->
                <div style="display: flex; align-items: center; margin-bottom: 12px; font-size: 14px; color: #4b5563;">
                  <svg style="width: 16px; height: 16px; margin-right: 8px; color: #9ca3af;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                  <span>${formatDate(project.startDate)} - ${formatDate(project.endDate)}</span>
                </div>

                ${
                  project.description
                    ? `
                <!-- Description -->
                <p style="margin: 0 0 16px 0; font-size: 14px; color: #4b5563; line-height: 1.4; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">${project.description}</p>
                `
                    : ''
                }

                <!-- Action Button -->
                <a href="/dashboard/projects/${project.id}" style="display: flex; align-items: center; justify-content: center; width: 100%; background: #ef4444; color: white; font-size: 14px; font-weight: 500; padding: 8px 12px; border-radius: 6px; text-decoration: none; transition: background-color 0.2s;" onmouseover="this.style.background='#dc2626'" onmouseout="this.style.background='#ef4444'">
                  <svg style="width: 16px; height: 16px; margin-right: 8px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                  View Details
                </a>
              </div>
            </div>
          `;
        };

        if (infoWindowRef.current) {
          infoWindowRef.current.setContent(createInfoWindowContent(project));
          infoWindowRef.current.open(mapInstanceRef.current, marker);
        }
      });

      markersRef.current.push(marker);
      bounds.extend(position);
    });

    // Fit map to show all markers
    if (projectsWithCoords.length > 1) {
      mapInstanceRef.current.fitBounds(bounds);
    } else if (projectsWithCoords.length === 1) {
      mapInstanceRef.current.setCenter({
        lat: projectsWithCoords[0].location!.coordinates!.lat,
        lng: projectsWithCoords[0].location!.coordinates!.lng,
      });
      mapInstanceRef.current.setZoom(15);
    }
  }, [projects, isLoaded, onProjectSelect]);

  if (!isLoaded) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <MapPin className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Loading Map...</h3>
          <p className="text-gray-500">
            Initializing Google Maps. Please make sure your API key is configured.
          </p>
        </div>
      </div>
    );
  }

  const projectsWithCoords = projects.filter(
    project => project.location?.coordinates?.lat && project.location?.coordinates?.lng
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {projectsWithCoords.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Projects with Locations</h3>
          <p className="text-gray-500 mb-6">
            Projects need location coordinates to be displayed on the map.
          </p>
        </div>
      ) : (
        <>
          {/* Map Controls */}
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MapPin className="h-5 w-5 text-red-400" />
                <span className="text-sm font-medium text-gray-900">
                  {projectsWithCoords.length} project{projectsWithCoords.length !== 1 ? 's' : ''} on
                  map
                </span>
              </div>
              <div className="text-sm text-gray-500">Click markers to view project details</div>
            </div>
          </div>

          {/* Map Container */}
          <div
            ref={mapRef}
            className="w-full h-96 md:h-[500px] lg:h-[600px]"
            style={{ minHeight: '400px' }}
          />
        </>
      )}
    </div>
  );
};

export default ProjectMapView;
